/**
 * « Tester » une animation JRAF comme un élève (moteur JRAF ; Studio admin).
 *
 * Lecteur avec ou sans accès à la partie interactive (sans accès : document
 * privé de ses scènes « À toi » par `stripInteractive`, comme le servira le
 * serveur, et carte « Débloquer ») ; « Ouvrir l'outil » ouvre le vrai outil
 * de la Boîte à outils ; intro et générique de l'app ou de la vidéo publiée
 * (« Retrouve d'autres leçons… ») ; rapport de validation (erreurs, défauts
 * de mise en page et de rythme, conseils).
 */

import { useMemo, useState } from 'react';
import {
  isQualityWarning,
  stripInteractive,
  type BookendMode,
  type JrafDocument,
  type JrafIssue,
} from '@jeraisonne/animation';
import { cn } from '@jeraisonne/ui';
import { AnimationPlayer } from '@jeraisonne/ui/components/animations/animation-player';
import { useSpeakerAvatars } from '@/lib/animations/speakers';
import { isToolId, toolById, type ToolId } from '@/features/toolbox/catalog';
import { ToolboxSheet } from '@/features/toolbox/toolbox-sheet';
import { useAnimationSoundtrack } from '@/lib/animations/audio';
import { animationAssetUrl } from '@/lib/animations/voice';

type Access = 'free' | 'full';

const ACCESS_LABEL: Record<Access, string> = {
  free: 'Sans accès',
  full: 'Avec accès',
};

const MODE_LABEL: Record<BookendMode, string> = {
  app: 'Dans l’app',
  video: 'Vidéo publiée',
};

function Choice({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={cn(
        'font-display min-h-11 rounded-full px-4 py-1 text-[13px] font-semibold transition-colors',
        active ? 'bg-primary-500 text-white' : 'bg-ink-100 text-ink-600 hover:bg-ink-200',
      )}
    >
      {children}
    </button>
  );
}

export function AnimationTestBench({ doc }: { doc: JrafDocument }) {
  const free = useMemo(() => stripInteractive(doc), [doc]);
  const [access, setAccess] = useState<Access>('free');
  const [mode, setMode] = useState<BookendMode>('app');
  const [tool, setTool] = useState<ToolId | null>(null);
  // remonte le lecteur à chaque changement d'accès (comme un nouvel élève)
  const [run, setRun] = useState(0);
  const toolDef = tool ? toolById(tool) : null;
  const soundtrack = useAnimationSoundtrack();
  const speakerAvatars = useSpeakerAvatars(doc);

  const choose = (a: Access) => {
    setAccess(a);
    setRun((r) => r + 1);
  };
  const chooseMode = (m: BookendMode) => {
    setMode(m);
    setRun((r) => r + 1);
  };

  // la vidéo ne montre pas la partie payante (« À toi » : questions, boîte à outils)
  const video = mode === 'video';

  return (
    <div>
      <div className="mb-3 flex flex-wrap gap-x-4 gap-y-2">
        {free.locked.length > 0 && !video && (
          <div role="group" aria-label="Accès à la partie interactive" className="flex gap-1.5">
            {(['free', 'full'] as const).map((a) => (
              <Choice key={a} active={access === a} onClick={() => choose(a)}>
                {ACCESS_LABEL[a]}
              </Choice>
            ))}
          </div>
        )}
        <div role="group" aria-label="Intro et générique" className="flex gap-1.5">
          {(['app', 'video'] as const).map((m) => (
            <Choice key={m} active={mode === m} onClick={() => chooseMode(m)}>
              {MODE_LABEL[m]}
            </Choice>
          ))}
        </div>
      </div>
      {video && free.locked.length > 0 && (
        <p className="text-ink-500 mb-2 text-[13px]" data-testid="bench-video-note">
          La vidéo s’arrête avant « À toi » (questions, boîte à outils) : cette partie reste dans
          l’app.
        </p>
      )}

      <AnimationPlayer
        key={`${access}-${mode}-${run}`}
        bookends={mode}
        soundtrack={soundtrack}
        speakerAvatars={speakerAvatars}
        assetUrl={animationAssetUrl}
        doc={video || access !== 'full' ? free.doc : doc}
        locked={video || access === 'full' ? [] : free.locked}
        onUnlock={() => choose('full')}
        onOpenTool={(id) => {
          if (isToolId(id)) setTool(id);
        }}
      />

      {tool && toolDef && (
        <ToolboxSheet
          context={{ level: null, families: toolDef.families, linked: [tool] }}
          initialTool={tool}
          featuredTitle="Outil de l’animation"
          onClose={() => setTool(null)}
        />
      )}
    </div>
  );
}

const ISSUE_TONE = {
  error: 'border-error-500/30 bg-error-50 text-error-600',
  quality: 'border-warning-500/40 bg-warning-50 text-ink-700',
  advice: 'border-ink-200 bg-ink-50 text-ink-700',
} as const;

type IssueLike = Pick<JrafIssue, 'path' | 'message' | 'code' | 'severity'>;

/**
 * Problèmes de validation (chemin, message) : erreurs (le document ne se joue
 * pas), défauts de mise en page, de rythme et de figure (un élève les verrait : la
 * génération les fait corriger), conseils.
 */
export function AnimationIssues({
  errors,
  warnings,
}: {
  errors: readonly IssueLike[];
  warnings: readonly IssueLike[];
}) {
  const quality = warnings.filter((w) => isQualityWarning(w));
  const advice = warnings.filter((w) => !isQualityWarning(w));
  if (errors.length === 0 && warnings.length === 0)
    return <p className="text-success-600 text-[13px] font-semibold">Aucun problème détecté.</p>;
  const groups = [
    ['error', errors, (n: number) => `${n} erreur${n > 1 ? 's' : ''}`],
    [
      'quality',
      quality,
      (n: number) => `${n} défaut${n > 1 ? 's' : ''} de mise en page, de rythme ou de figure`,
    ],
    ['advice', advice, (n: number) => `${n} conseil${n > 1 ? 's' : ''}`],
  ] as const;
  return (
    <div className="space-y-2">
      {groups.map(([kind, list, label]) =>
        list.length === 0 ? null : (
          <details
            key={kind}
            open={kind !== 'advice'}
            className={cn('rounded-xl border p-3', ISSUE_TONE[kind])}
          >
            <summary className="cursor-pointer text-[13px] font-semibold">
              {label(list.length)}
            </summary>
            <ul className="mt-2 space-y-1 text-[13px]">
              {list.map((e, i) => (
                <li key={i}>
                  <code className="text-[12px]">{e.path || '(document)'}</code> — {e.message}
                </li>
              ))}
            </ul>
          </details>
        ),
      )}
    </div>
  );
}
